/**
 * VibeKonnectSettingsModal — one modal, four tabs:
 *   API Keys   — AES-256 vault (Anthropic / OpenAI / GitHub / Stripe)
 *   Deploy     — deploy adapter targets (Vercel / Railway / Netlify / Plesk)
 *   MCP        — community MCP server registry
 *   Artifacts  — persistent plan.md / design-spec.md / INTEGRATIONS.md
 *
 * Server enforces super_admin; browser never sees plaintext keys after save.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  KeyRound, Rocket, PlugZap, FileText, Loader2, Trash2, Save, Plus, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = API + '/api/dev-console/vibeconnect';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('konnect_access_token') || ''}`,
});

// ─────────────────────────── KEYS TAB ───────────────────────────
function KeysTab() {
  const [keys, setKeys] = useState([]);
  const [providers, setProviders] = useState(['anthropic', 'openai', 'github', 'stripe']);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ provider: 'anthropic', key_name: '', plaintext: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE + '/keys', { headers: authHeaders() });
      setKeys(data.keys || []);
      if (data.supported_providers) setProviders(data.supported_providers);
    } catch (e) {
      toast.error('Failed to load keys: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (form.plaintext.trim().length < 8) { toast.error('Key looks too short'); return; }
    if (!form.key_name.trim()) { toast.error('Give this key a name'); return; }
    setSaving(true);
    try {
      await axios.post(BASE + '/keys', form, { headers: authHeaders() });
      toast.success(`${form.provider} key saved (encrypted)`);
      setForm({ provider: form.provider, key_name: '', plaintext: '' });
      load();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this key? Cannot be undone.')) return;
    try {
      await axios.delete(BASE + '/keys/' + id, { headers: authHeaders() });
      toast.success('Key deleted');
      load();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid="vk-settings-keys">
      <div className="rounded-md bg-emerald-950/30 border border-emerald-800/50 p-3 text-xs text-emerald-200 flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          Keys are encrypted with AES-256-GCM before storage. Plaintext never leaves this device
          and is never returned to the browser after save. The Anthropic key you add here overrides
          the server env for your chats.
        </div>
      </div>

      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Add a new key</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            className="h-9 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-100 text-sm px-2"
            data-testid="vk-key-provider"
          >
            {providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input
            placeholder="Label (e.g. 'Personal Anthropic')"
            value={form.key_name}
            onChange={(e) => setForm({ ...form, key_name: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 text-sm"
            data-testid="vk-key-name"
          />
          <Input
            type="password"
            placeholder="sk-ant-... / sk-... / ghp_..."
            value={form.plaintext}
            onChange={(e) => setForm({ ...form, plaintext: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 text-sm font-mono"
            data-testid="vk-key-plaintext"
          />
        </div>
        <Button onClick={save} disabled={saving} size="sm" data-testid="vk-key-save"
          className="bg-emerald-600 hover:bg-emerald-500 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Encrypt & Save
        </Button>
      </div>

      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Stored keys ({keys.length})</div>
        {loading && <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>}
        {!loading && keys.length === 0 && <div className="text-zinc-500 text-xs italic">No keys stored yet. VibeKonnect falls back to the server env.</div>}
        {keys.map((k) => (
          <div key={k._id} className="flex items-center gap-2 p-2 rounded border border-zinc-800 bg-zinc-950/40 text-sm">
            <Badge variant="outline" className="font-mono text-[10px] border-zinc-700 text-zinc-400">{k.provider}</Badge>
            <div className="flex-1 min-w-0">
              <div className="text-zinc-200 truncate">{k.key_name}</div>
              <div className="text-[10px] text-zinc-500 font-mono">
                added {new Date((k.created_at || 0) * 1000).toLocaleDateString()}
                {k.last_used_at ? ` · last used ${new Date(k.last_used_at * 1000).toLocaleDateString()}` : ' · never used'}
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => del(k._id)}
              className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-950/40"
              data-testid={`vk-key-del-${k._id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────── DEPLOY TAB ───────────────────────────
function DeployTab() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE + '/deploy/targets', { headers: authHeaders() });
      setTargets(data.targets || []);
    } catch (e) {
      toast.error('Failed to load targets: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const trigger = async (target) => {
    if (target.key === 'plesk') {
      toast.info('Use the Deploy button in the chat header for the primary Plesk pipeline.');
      return;
    }
    setTriggering(target.key);
    try {
      const { data } = await axios.post(BASE + '/deploy', { target: target.key, git_ref: 'main' }, { headers: authHeaders() });
      toast.success(`${target.label}: deployment triggered${data.url ? ` → ${data.url}` : ''}`);
    } catch (e) {
      toast.error(`${target.label} failed: ${e.response?.data?.detail || e.message}`);
    } finally { setTriggering(null); }
  };

  return (
    <div className="space-y-3" data-testid="vk-settings-deploy">
      <div className="text-xs text-zinc-400">
        Plesk stays your primary pipeline. Add secondary targets by setting their env vars in <code className="text-emerald-300">backend/.env</code>.
      </div>
      {loading && <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>}
      {!loading && targets.map((t) => (
        <div key={t.key} className="flex items-center gap-3 p-3 rounded border border-zinc-800 bg-zinc-950/40">
          <Rocket className={'h-4 w-4 ' + (t.configured ? 'text-emerald-400' : 'text-zinc-600')} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-100 flex items-center gap-2">
              {t.label}
              {t.note && <Badge variant="outline" className="text-[9px] border-emerald-700 text-emerald-300">{t.note}</Badge>}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              env: <code>{t.env_key}</code> · status: {t.configured ? <span className="text-emerald-400">configured</span> : <span className="text-zinc-600">missing env var</span>}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!t.configured || triggering === t.key || t.key === 'plesk'}
            onClick={() => trigger(t)}
            className="h-8 text-xs border-zinc-700 text-zinc-200 hover:bg-emerald-950/40 hover:border-emerald-700 disabled:opacity-40"
            data-testid={`vk-deploy-${t.key}`}
          >
            {triggering === t.key ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Rocket className="h-3.5 w-3.5 mr-1" />}
            Deploy
          </Button>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── MCP TAB ───────────────────────────
function MCPTab() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', endpoint: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE + '/mcp', { headers: authHeaders() });
      setServers(data.servers || []);
    } catch (e) {
      toast.error('Failed to load MCP servers');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.endpoint.trim()) { toast.error('Name and endpoint are required'); return; }
    setSaving(true);
    try {
      await axios.post(BASE + '/mcp', form, { headers: authHeaders() });
      toast.success('MCP server added');
      setForm({ name: '', endpoint: '', description: '' });
      load();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try {
      await axios.patch(BASE + '/mcp/' + s._id, { enabled: !s.enabled }, { headers: authHeaders() });
      load();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this MCP server?')) return;
    try {
      await axios.delete(BASE + '/mcp/' + id, { headers: authHeaders() });
      toast.success('Removed');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="vk-settings-mcp">
      <div className="text-xs text-zinc-400">
        Register community <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">MCP</a> servers your agents can call as tools. Endpoints are stored per-user; auth headers are stored encrypted server-side.
      </div>
      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Add server</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input placeholder="Name (e.g. Sentry)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 text-sm"
            data-testid="vk-mcp-name" />
          <Input placeholder="Endpoint URL (https://…)" value={form.endpoint}
            onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 text-sm font-mono"
            data-testid="vk-mcp-endpoint" />
        </div>
        <Textarea placeholder="Optional description" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-zinc-100 text-sm min-h-[60px]"
          data-testid="vk-mcp-desc" />
        <Button size="sm" onClick={save} disabled={saving} data-testid="vk-mcp-save"
          className="bg-emerald-600 hover:bg-emerald-500 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
          Add server
        </Button>
      </div>
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Registered ({servers.length})</div>
        {loading && <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>}
        {!loading && servers.length === 0 && <div className="text-zinc-500 text-xs italic">No MCP servers configured.</div>}
        {servers.map((s) => (
          <div key={s._id} className="flex items-center gap-2 p-2 rounded border border-zinc-800 bg-zinc-950/40 text-sm">
            <PlugZap className={'h-4 w-4 ' + (s.enabled ? 'text-emerald-400' : 'text-zinc-600')} />
            <div className="flex-1 min-w-0">
              <div className="text-zinc-200 truncate">{s.name}</div>
              <div className="text-[10px] text-zinc-500 font-mono truncate">{s.endpoint_url}</div>
              {s.description && <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{s.description}</div>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => toggle(s)}
              className={'h-7 text-[10px] ' + (s.enabled ? 'text-emerald-300' : 'text-zinc-500')}>
              {s.enabled ? 'ON' : 'OFF'}
            </Button>
            <Button size="icon" variant="ghost" onClick={() => del(s._id)}
              className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-950/40">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────── ARTIFACTS TAB ───────────────────────────
const ARTIFACT_NAMES = ['plan.md', 'design-spec.md', 'INTEGRATIONS.md'];

function ArtifactsTab() {
  const [selected, setSelected] = useState('plan.md');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (name) => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE + '/artifacts/' + encodeURIComponent(name), { headers: authHeaders() });
      setContent(data.content || '');
    } catch (e) {
      toast.error('Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(selected); }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(BASE + '/artifacts/' + encodeURIComponent(selected),
        { content, agent: 'user' }, { headers: authHeaders() });
      toast.success(`${selected} saved`);
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="vk-settings-artifacts">
      <div className="text-xs text-zinc-400">
        Persistent artifacts survive across sessions. Agents read and update these automatically.
      </div>
      <div className="flex gap-1">
        {ARTIFACT_NAMES.map((n) => (
          <button key={n} onClick={() => setSelected(n)}
            className={'px-2.5 py-1 rounded text-[11px] font-mono border ' +
              (selected === n
                ? 'bg-emerald-950/60 border-emerald-700 text-emerald-200'
                : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200')}
            data-testid={`vk-artifact-tab-${n}`}
          >
            <FileText className="inline h-3 w-3 mr-1" />{n}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : (
        <>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[280px] font-mono text-xs"
            placeholder={`${selected} is empty. Agents will populate it as they work.`}
            data-testid="vk-artifact-editor" />
          <Button size="sm" onClick={save} disabled={saving} data-testid="vk-artifact-save"
            className="bg-emerald-600 hover:bg-emerald-500 text-white h-8">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save {selected}
          </Button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── ROOT ───────────────────────────
export default function VibeKonnectSettingsModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="vk-settings-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            VibeKonnect Settings
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            AES-256 key vault · Deploy targets · MCP servers · Persistent artifacts
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="keys" className="w-full mt-2">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="keys" data-testid="vk-tab-keys" className="data-[state=active]:bg-emerald-950/60 data-[state=active]:text-emerald-200"><KeyRound className="h-3.5 w-3.5 mr-1" />Keys</TabsTrigger>
            <TabsTrigger value="deploy" data-testid="vk-tab-deploy" className="data-[state=active]:bg-emerald-950/60 data-[state=active]:text-emerald-200"><Rocket className="h-3.5 w-3.5 mr-1" />Deploy</TabsTrigger>
            <TabsTrigger value="mcp" data-testid="vk-tab-mcp" className="data-[state=active]:bg-emerald-950/60 data-[state=active]:text-emerald-200"><PlugZap className="h-3.5 w-3.5 mr-1" />MCP</TabsTrigger>
            <TabsTrigger value="artifacts" data-testid="vk-tab-artifacts" className="data-[state=active]:bg-emerald-950/60 data-[state=active]:text-emerald-200"><FileText className="h-3.5 w-3.5 mr-1" />Artifacts</TabsTrigger>
          </TabsList>
          <TabsContent value="keys" className="mt-3"><KeysTab /></TabsContent>
          <TabsContent value="deploy" className="mt-3"><DeployTab /></TabsContent>
          <TabsContent value="mcp" className="mt-3"><MCPTab /></TabsContent>
          <TabsContent value="artifacts" className="mt-3"><ArtifactsTab /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
